/**
 * Investor Landing Page
 * English only, clean presentation, clear value proposition
 * Goal: Investor understands KEIKKA in under 30 seconds
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Globe, Users, Briefcase, TrendingUp, Shield, 
  ChevronRight, Play, Mail, ArrowRight, Building2,
  Target, DollarSign, MapPin
} from 'lucide-react';

// Force English for investor page
const CONTENT = {
  hero: {
    badge: 'SERIES A READY',
    title: 'The Future of Work',
    subtitle: 'Across Borders',
    description: 'KEIKKA connects skilled workers with opportunities across the Nordics and beyond. One platform. No borders.',
    cta1: 'Watch Demo',
    cta2: 'Request Pitch Deck'
  },
  problem: {
    title: 'The Problem',
    subtitle: '€200B+ Labor Market Gap',
    points: [
      {
        icon: MapPin,
        title: 'Fragmented Markets',
        description: 'Labor markets in the Nordics are siloed. Workers in Estonia can\'t easily find work in Finland.'
      },
      {
        icon: Users,
        title: 'Skills Shortage',
        description: '72% of Nordic employers report difficulty filling positions. Talent exists—it\'s just not connected.'
      },
      {
        icon: Building2,
        title: 'Outdated Infrastructure',
        description: 'Legacy job boards don\'t support cross-border hiring, instant matching, or verified credentials.'
      }
    ]
  },
  solution: {
    title: 'Our Solution',
    subtitle: 'KEIKKA — Work Without Borders',
    features: [
      {
        icon: Globe,
        title: 'Cross-Border Marketplace',
        description: 'Jobs, services, and gig work across 6 countries with native language support.'
      },
      {
        icon: Shield,
        title: 'Verified Profiles',
        description: 'ID verification, skill certification, and reviews create trust at scale.'
      },
      {
        icon: TrendingUp,
        title: 'AI-Powered Matching',
        description: 'Smart algorithms connect workers with relevant opportunities instantly.'
      }
    ]
  },
  traction: {
    title: 'Traction',
    metrics: [
      { value: '15K+', label: 'Active Users', growth: '+340% YoY' },
      { value: '€2.4M', label: 'GMV Facilitated', growth: '+180% QoQ' },
      { value: '6', label: 'Countries Live', growth: 'Nordics + Russia' },
      { value: '4.8★', label: 'User Rating', growth: 'App Store' }
    ]
  },
  monetization: {
    title: 'Business Model',
    subtitle: 'Multiple Revenue Streams',
    streams: [
      { name: 'Platform Fee', description: '10-15% on facilitated transactions', revenue: '€180K ARR' },
      { name: 'Premium Listings', description: 'Boost visibility for employers', revenue: '€45K ARR' },
      { name: 'Verified Badges', description: 'Worker certification fees', revenue: '€30K ARR' },
      { name: 'Enterprise API', description: 'Integration for staffing agencies', revenue: 'Pipeline' }
    ]
  },
  team: {
    title: 'Team',
    subtitle: 'Built by operators who know the market'
  },
  ask: {
    title: 'The Ask',
    amount: '€3M Series A',
    use: [
      'Expand to 5 new markets',
      'Scale engineering team',
      'Launch mobile apps',
      'Enterprise sales push'
    ]
  },
  cta: {
    title: 'Let\'s Talk',
    subtitle: 'See why KEIKKA is the next big platform in European labor markets.',
    button: 'Request Pitch Deck',
    demo: 'Or try the demo'
  }
};

export default function InvestorPage() {
  const [searchParams] = useSearchParams();
  const demoMode = searchParams.get('demo') === 'true';

  const handleRequestPitch = () => {
    window.location.href = 'mailto:invest@keikka.com?subject=Pitch%20Deck%20Request&body=Hi%20KEIKKA%20team,%0A%0AI\'m%20interested%20in%20learning%20more%20about%20KEIKKA.%0A%0AMy%20name:%20%0ARole:%20%0AFund/Company:%20%0A%0AThanks!';
  };

  const handleWatchDemo = () => {
    // Navigate to demo mode
    window.location.href = '/?demo=true';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold tracking-tight">
            KEIKKA
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-300 hover:text-white"
              onClick={handleWatchDemo}
            >
              View Demo
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleRequestPitch}
            >
              Request Deck
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 bg-blue-600/20 text-blue-400 border-blue-500/30 px-4 py-1">
            {CONTENT.hero.badge}
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-4 tracking-tight">
            {CONTENT.hero.title}
            <span className="block text-blue-400">{CONTENT.hero.subtitle}</span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            {CONTENT.hero.description}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 px-8 h-14 text-lg"
              onClick={handleWatchDemo}
            >
              <Play className="h-5 w-5 mr-2" />
              {CONTENT.hero.cta1}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-slate-600 text-white hover:bg-slate-800 px-8 h-14 text-lg"
              onClick={handleRequestPitch}
            >
              <Mail className="h-5 w-5 mr-2" />
              {CONTENT.hero.cta2}
            </Button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{CONTENT.problem.title}</h2>
            <p className="text-xl text-blue-400">{CONTENT.problem.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {CONTENT.problem.points.map((point, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <point.icon className="h-10 w-10 text-red-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{point.title}</h3>
                  <p className="text-slate-400">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{CONTENT.solution.title}</h2>
            <p className="text-xl text-blue-400">{CONTENT.solution.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {CONTENT.solution.features.map((feature, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-blue-600/10 to-blue-800/10 border-blue-500/20">
                <CardContent className="p-6">
                  <feature.icon className="h-10 w-10 text-blue-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Traction */}
      <section className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">{CONTENT.traction.title}</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {CONTENT.traction.metrics.map((metric, idx) => (
              <Card key={idx} className="bg-slate-900/50 border-slate-700 text-center">
                <CardContent className="p-6">
                  <p className="text-4xl font-bold text-white mb-1">{metric.value}</p>
                  <p className="text-slate-400 mb-2">{metric.label}</p>
                  <Badge variant="outline" className="text-green-400 border-green-500/30">
                    {metric.growth}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Monetization */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{CONTENT.monetization.title}</h2>
            <p className="text-xl text-slate-400">{CONTENT.monetization.subtitle}</p>
          </div>
          
          <div className="space-y-4">
            {CONTENT.monetization.streams.map((stream, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{stream.name}</h4>
                      <p className="text-sm text-slate-400">{stream.description}</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/30">
                    {stream.revenue}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Ask */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">{CONTENT.ask.title}</h2>
          <p className="text-5xl font-bold text-blue-400 mb-8">{CONTENT.ask.amount}</p>
          
          <div className="grid md:grid-cols-2 gap-4 max-w-xl mx-auto mb-8">
            {CONTENT.ask.use.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 text-left bg-slate-800/50 rounded-lg p-4">
                <Target className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <span className="text-slate-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{CONTENT.cta.title}</h2>
          <p className="text-xl text-slate-400 mb-10">{CONTENT.cta.subtitle}</p>
          
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 px-10 h-16 text-xl"
            onClick={handleRequestPitch}
          >
            {CONTENT.cta.button}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          
          <p className="mt-6 text-slate-500">
            {CONTENT.cta.demo}{' '}
            <Link to="/?demo=true" className="text-blue-400 hover:underline">
              keikka.com?demo=true
            </Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <p>© 2026 KEIKKA OÜ. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="mailto:invest@keikka.com" className="hover:text-white">invest@keikka.com</a>
            <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
